import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Product } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { getProductBySlug, getRelatedProducts } from '@/lib/data/products'
import { SplitChars } from '@/components/motion/split-text'
import Marquee from '@/components/motion/marquee'
import ProductDetail from '@/components/shop/product-detail'
import ProductCard from '@/components/shop/product-card'
import Button from '@/components/generals/button'
import Star from '@/components/generals/star'

interface ProductPageProps {
    product: Product
    related: Array<Product>
}

const PROMISES = ['Numbered run of 300', 'Made in Portugal', 'Free repairs for life', 'Designed in Berlin']

const ProductPage: FC<ProductPageProps> = ({ product, related }): ReactNode => {
    return (
        <Layout>
            <Seo
                title={product.name}
                description={product.summary || `${product.name}, cut in a numbered run of 300 at Elio and repaired for life.`}
                ogImage={product.image || undefined}
                type='product'
            />

            <ProductDetail product={product} />

            <section id='about-the-piece' aria-labelledby='about-piece-title' className='border-t border-line py-24 gutter md:py-32'>
                <div className='grid gap-10 md:grid-cols-12 md:gap-8'>
                    <p data-reveal='fade' className='label-mono text-acid md:col-span-3'>
                        (About the piece)
                    </p>

                    <div className='md:col-span-8 md:col-start-5 lg:col-span-7 lg:col-start-5'>
                        <h2 id='about-piece-title' data-reveal='chars' className='display-l uppercase'>
                            <SplitChars text='The piece' />
                        </h2>

                        {product.description ? (
                            <p data-reveal='fade' className='mt-8 max-w-[52ch] lead text-bone/85'>
                                <span data-mdlfy-cms={`products:${product.id}:description`}>{product.description}</span>
                            </p>
                        ) : null}

                        <p data-reveal='fade' className='mt-5 max-w-[56ch] body-copy text-bone/65'>
                            Every Elio piece is made to be worn daily for a decade. We test seams, trims and fabrics until they fail — then we make them stronger.
                        </p>

                        <dl data-reveal='fade' className='mt-10 grid border-t border-line sm:grid-cols-2 sm:gap-x-8'>
                            {product.material ? (
                                <div className='flex justify-between gap-4 border-b border-line py-3'>
                                    <dt className='meta-mono text-ash'>Material</dt>
                                    <dd className='label-mono text-right'><span data-mdlfy-cms={`products:${product.id}:material`}>{product.material}</span></dd>
                                </div>
                            ) : null}

                            {product.fit ? (
                                <div className='flex justify-between gap-4 border-b border-line py-3'>
                                    <dt className='meta-mono text-ash'>Fit</dt>
                                    <dd className='label-mono text-right'><span data-mdlfy-cms={`products:${product.id}:fit`}>{product.fit}</span></dd>
                                </div>
                            ) : null}

                            <div className='flex justify-between gap-4 border-b border-line py-3'>
                                <dt className='meta-mono text-ash'>Made in</dt>
                                <dd className='label-mono text-right'>Portugal</dd>
                            </div>

                            {product.style_code ? (
                                <div className='flex justify-between gap-4 border-b border-line py-3'>
                                    <dt className='meta-mono text-ash'>Style code</dt>
                                    <dd className='label-mono text-right'><span data-mdlfy-cms={`products:${product.id}:style_code`}>{product.style_code}</span></dd>
                                </div>
                            ) : null}
                        </dl>
                    </div>
                </div>
            </section>

            <div aria-hidden='true' className='border-y border-line py-6'>
                <Marquee speed={48} velocity={0.16}>
                    {PROMISES.map(item => (
                        <span key={item} className='flex items-center gap-10 pr-10 font-heading text-[clamp(44px,7vw,112px)] leading-none text-outline'>
                            {item}

                            <Star className='text-acid' />
                        </span>
                    ))}
                </Marquee>
            </div>

            {related.length ? (
                <section id='complete-the-look' aria-labelledby='complete-title' className='py-24 gutter md:py-32'>
                    <div className='flex flex-wrap items-end justify-between gap-6'>
                        <h2 id='complete-title' data-reveal='chars' className='display-l uppercase'>
                            <SplitChars text='Complete the look' />
                        </h2>

                        <div data-reveal='fade'>
                            <Button href='/shop' variant='ghost'>
                                View all
                            </Button>
                        </div>
                    </div>

                    <div className='mt-12 grid grid-cols-2 gap-x-3 gap-y-10 sm:gap-x-5 lg:grid-cols-4 lg:gap-x-6'>
                        {related.map(item => (
                            <ProductCard key={item.id} product={item} sizes='(min-width: 1024px) 24vw, 50vw' />
                        ))}
                    </div>
                </section>
            ) : null}
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<ProductPageProps, { slug: string }> = async ({ params }) => {
    const slug = params?.slug

    if (!slug) return { notFound: true }

    const product = await getProductBySlug(slug)

    if (!product) return { notFound: true }

    const related = await getRelatedProducts(product, 4).catch(() => [])

    return { props: { product, related } }
}

export default ProductPage
